import { supabase } from '@/integrations/supabase/client';

export interface ExtractedLocation {
  text: string;
  lat: number;
  lng: number;
  city: string;
  state?: string;
}

/**
 * Extract city/location names from user text and geocode them to lat/lng coordinates.
 * Uses pattern matching to find location references like "in Boston", "for Chicago", etc.
 */
export async function extractAndGeocodeLocations(text: string): Promise<ExtractedLocation[]> {
  if (!text || text.trim().length < 3) {
    return [];
  }

  // Common patterns for location mentions in questions
  const patterns = [
    /(?:in|for|at|near|around|from|to)\s+([A-Z][a-zA-Z]+(?:\s+[A-Z][a-zA-Z]+)?(?:,\s*[A-Z]{2})?)/gi,
    /([A-Z][a-zA-Z]+(?:,\s*[A-Z]{2})?)\s+(?:weather|forecast|temperature|conditions)/gi,
    /weather\s+(?:in|for|at)?\s*([A-Z][a-zA-Z]+(?:\s+[A-Z][a-zA-Z]+)?(?:,\s*[A-Z]{2})?)/gi,
  ];

  const potentialLocations = new Set<string>();

  for (const pattern of patterns) {
    let match;
    while ((match = pattern.exec(text)) !== null) {
      const location = match[1]?.trim();
      if (location && location.length > 2) {
        potentialLocations.add(location);
      }
    }
  }

  if (potentialLocations.size === 0) {
    return [];
  }

  const results: ExtractedLocation[] = [];

  // Geocode each potential location
  for (const locationText of potentialLocations) {
    try {
      const { data, error } = await supabase.functions.invoke('geocoding', {
        body: { query: locationText }
      });

      if (error || !data?.features?.length) {
        continue;
      }

      const feature = data.features[0];
      const [lng, lat] = feature.center || feature.geometry?.coordinates || [];

      if (lat && lng) {
        // Extract city and state from context
        const context = feature.context || [];
        const regionContext = context.find((c: { id: string }) => c.id?.startsWith('region'));
        const placeContext = context.find((c: { id: string }) => c.id?.startsWith('place'));

        results.push({
          text: locationText,
          lat,
          lng,
          city: placeContext?.text || feature.text || locationText,
          state: regionContext?.short_code?.replace('US-', '') || regionContext?.text,
        });
      }
    } catch (err) {
      console.warn(`Failed to geocode location "${locationText}":`, err);
    }
  }

  return results;
}
